import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { timesheetImportUrl } from 'router/url';
import ControlledDatePicker from 'components/ControlledDatePicker';
import { ContentDialog } from 'components/Dialog/ContentDialog';
import FilesUploadArea, {
  SPREADSHEET_EXTENSIONS,
} from 'components/FileUploadArea/FilesUploadArea';
import { getReportingErrorMessage } from 'modules/OrderReporting/utils';
import { postFetch } from 'utils/fetchFunctions';
import { UploadDialogForm, UploadDialogProps } from './UploadDialog.types';
import { ContentWrapper, UploadDialogWrapper } from './UploadDialog.css';

const yearMonthFormat = 'yyyy-MM';

export const UploadDialog = ({
  isUploadDialogOpen,
  setUploadDialogOpen,
  onDialogSubmit,
}: UploadDialogProps) => {
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);

  const intl = useIntl();
  const { control, getValues } = useForm<UploadDialogForm>({
    defaultValues: {
      yearMonth: format(new Date(), yearMonthFormat),
    },
  });

  const handleSetSelectedFiles = (uploadFiles: File[]) => {
    setSelectedFiles(uploadFiles);
  };

  const handleSubmitForm = async () => {
    const { yearMonth } = getValues();

    const uploadResults = await Promise.all(
      selectedFiles.map(async (file) => {
        const requestBodyData = {
          file,
          yearMonth: format(new Date(yearMonth), yearMonthFormat),
        };

        const formData = new FormData();

        for (const [key, value] of Object.entries(requestBodyData)) {
          formData.append(key, value);
        }

        try {
          const response = await postFetch({
            url: `${timesheetImportUrl}`,
            body: formData,
            label: 'TIMESHEET_IMPORT',
            contentType: 'formdata',
            showErrorToast: false,
          });

          if (response.ok) {
            toast.success(
              intl.formatMessage(
                { id: 'TIMESHEET_IMPORT.TOAST_ACCEPT' },
                { fileName: file.name },
              ),
            );
            return true;
          } else {
            toast.error(
              getReportingErrorMessage(
                response.data,
                intl,
                'TIMESHEET_IMPORT.TOAST_DENIED',
                { fileName: file.name },
              ),
            );
            return false;
          }
        } catch (e) {
          toast.error(
            intl.formatMessage(
              { id: 'TIMESHEET_IMPORT.TOAST_DENIED' },
              { fileName: file.name },
            ),
          );
          return false;
        }
      }),
    );
    onDialogSubmit();
    if (uploadResults.every(Boolean)) {
      setUploadDialogOpen(false);
    }
  };

  return (
    <ContentDialog
      isOpen={isUploadDialogOpen}
      setIsOpen={setUploadDialogOpen}
      headerTitle="TIMESHEET_IMPORT.UPLOAD_TIMESHEET"
      confirmButtonContent="DIALOG.SEND"
      handleConfirm={handleSubmitForm}
      disableConfirmButton={!selectedFiles.length}
    >
      <ContentWrapper>
        <form onSubmit={handleSubmitForm}>
          <FilesUploadArea
            handleFileChange={handleSetSelectedFiles}
            allowedExtensions={SPREADSHEET_EXTENSIONS}
          />
          <UploadDialogWrapper>
            <ControlledDatePicker
              name="yearMonth"
              label={intl.formatMessage({
                id: 'TIMESHEET_IMPORT.UPLOAD_DIALOG.DATE',
              })}
              views={['year', 'month']}
              format={yearMonthFormat}
              control={control}
            />
          </UploadDialogWrapper>
        </form>
      </ContentWrapper>
    </ContentDialog>
  );
};
